import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react'
import api, { Locale, ProjectId } from '@monorepo/api'
import { BaseCdaUrl } from '@monorepo/service'
import Spinner from '../components/LoginForm/Spinner'
import {
  DEFAULT_GEO,
  GEO_STORAGE_KEY,
  GEO_RUNTIME_FALLBACKS,
  LANGUAGE_META,
  LANGUAGE_STORAGE_KEY,
  LANGUAGE_TO_GEO,
  type SupportedGeo,
  type GeoRuntimeConfig,
  getGeoRuntimeConfig,
  getResolvedGeoStateForGeo,
  type ProjectInfoEntry,
  resolveGeoState,
  type ResolvedGeoState,
  type SupportedLanguage,
  getProjectInfoByGeo,
} from './config'

type GeoRuntimeContextValue = ResolvedGeoState & {
  geoConfig: GeoRuntimeConfig
  languageMeta: (typeof LANGUAGE_META)[SupportedLanguage]
  getGeoConfig: (geo: SupportedGeo) => GeoRuntimeConfig
  setGeo: (geo: SupportedGeo) => void
  setLanguage: (language: SupportedLanguage) => void
}

const DEFAULT_STATE: ResolvedGeoState = {
  geo: DEFAULT_GEO,
  language: GEO_RUNTIME_FALLBACKS[DEFAULT_GEO].defaultLanguage,
  projectId: ProjectId.get(),
  baseCdaUrl: BaseCdaUrl.get(),
  allowedRegions: [DEFAULT_GEO],
  allowedLanguages: [GEO_RUNTIME_FALLBACKS[DEFAULT_GEO].defaultLanguage],
}

const GeoRuntimeContext = createContext<GeoRuntimeContextValue>({
  ...DEFAULT_STATE,
  geoConfig: getGeoRuntimeConfig([], DEFAULT_STATE.geo),
  getGeoConfig: (geo) => getGeoRuntimeConfig([], geo),
  languageMeta: LANGUAGE_META[DEFAULT_STATE.language],
  setGeo: () => undefined,
  setLanguage: () => undefined,
})

const persistState = ({ geo, language, projectId, baseCdaUrl }: ResolvedGeoState) => {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(GEO_STORAGE_KEY, geo)
  window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language)
  window.document.documentElement.dataset.geo = geo.toLowerCase()
  window.document.documentElement.lang = language
  Locale.set(language)
  if (projectId) ProjectId.set(projectId)
  if (baseCdaUrl) BaseCdaUrl.set(baseCdaUrl)
}

export const GeoRuntimeProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [projectInfo, setProjectInfo] = useState<ProjectInfoEntry[]>([])
  const [state, setState] = useState<ResolvedGeoState>(() => DEFAULT_STATE)
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    if (!isReady) return
    console.group('Geo state changed')
    console.log('Brand Geo:', state.geo)
    console.log('projectId:', state.projectId)
    console.log('Cda Url:', state.baseCdaUrl)
    console.log({ state })
    console.groupEnd()
  }, [state, isReady])

  useEffect(() => {
    const APP_PROJECT_INFO_URL =
      (typeof window !== 'undefined' && window.env.APP_PROJECT_INFO_URL) || ''
    const projectInfoRequest = APP_PROJECT_INFO_URL
      ? fetch(APP_PROJECT_INFO_URL, { method: 'GET' }).then(
          (response) => response.json() as Promise<ProjectInfoEntry[]>,
        )
      : Promise.resolve([] as ProjectInfoEntry[])

    Promise.allSettled([
      projectInfoRequest,
      api.cda.publicCdaV2IpInfoRetrieve({ cache: 'no-store' }),
    ])
      .then(([projectInfoResult, ipInfoResult]) => {
        if (projectInfoResult.status !== 'fulfilled') throw projectInfoResult.reason

        const projectInfo = projectInfoResult.value
        const ipAlpha2 =
          ipInfoResult.status === 'fulfilled' ? ipInfoResult.value.data.alpha_2 : undefined

        setProjectInfo(projectInfo)
        const state = resolveGeoState({
          search: window.location.search,
          storedGeo: window.localStorage.getItem(GEO_STORAGE_KEY),
          storedLanguage: window.localStorage.getItem(LANGUAGE_STORAGE_KEY),
          projectInfo,
          ipAlpha2,
        })

        console.log({ state })

        setState(state)
        persistState(state)
      })
      .catch((error) => {
        console.error(error)
      })
      .finally(() => {
        setIsReady(true)
      })
  }, [])

  const setLanguage = useCallback(
    (nextLanguage: SupportedLanguage) => {
      setState((prev) => {
        if (!prev.allowedLanguages.includes(nextLanguage)) {
          return prev
        }

        const nextGeoFromLanguage = LANGUAGE_TO_GEO[nextLanguage]
        const geo =
          nextGeoFromLanguage && prev.allowedRegions.includes(nextGeoFromLanguage)
            ? nextGeoFromLanguage
            : prev.geo
        const state = getResolvedGeoStateForGeo({
          geo,
          language: nextLanguage,
          projectInfo,
          allowedRegions: prev.allowedRegions,
          allowedLanguages: prev.allowedLanguages,
        })
        persistState(state)

        return state
      })
    },
    [projectInfo],
  )

  const setGeo = useCallback(
    (geo: SupportedGeo) => {
      console.log('setGeo', geo)

      console.trace('setGeo stack')
      setState((prev) => {
        const infoByGeo = getProjectInfoByGeo(projectInfo, geo)

        const state = {
          geo,
          language: prev.language,
          projectId: infoByGeo?.project_id,
          baseCdaUrl: infoByGeo?.cda,
          allowedRegions: prev.allowedRegions,
          allowedLanguages: prev.allowedLanguages,
        }
        persistState(state)

        return state
      })
    },
    [projectInfo],
  )

  const getGeoConfig = useCallback(
    (geo: SupportedGeo) => getGeoRuntimeConfig(projectInfo, geo),
    [projectInfo],
  )

  const value = useMemo<GeoRuntimeContextValue>(
    () => ({
      ...state,
      geoConfig: getGeoConfig(state.geo),
      getGeoConfig,
      languageMeta: LANGUAGE_META[state.language],
      setGeo,
      setLanguage,
    }),
    [getGeoConfig, setGeo, setLanguage, state],
  )

  return (
    <GeoRuntimeContext.Provider value={value}>
      {isReady ? children : <Spinner />}
    </GeoRuntimeContext.Provider>
  )
}

export { GeoRuntimeContext }
